import {
  IMAGE_SCENE,
  MUSIC_SCENE,
  TYPES_CONFIG,
  VIDEO_SCENE,
  getTextBoxInitialState,
} from './draft-scenes'
import { TYPE_SUBSCENE_MUSIC } from './type-subscene'

/**
 * Ready-made scene layouts for the editor's "Add scene" picker
 * (components/common/AddSceneDropdown.vue). Extracted here so that picker
 * and the brief/script-to-scenes flow (services/video-scenes/script-to-scenes.js)
 * build the SAME scene types instead of each declaring its own look-alike
 * literals. (POR-13874)
 *
 * Each function returns a fresh set of rows — getTextBoxInitialState
 * deep-copies internally, and the spreads below copy IMAGE_SCENE's fields —
 * so callers are free to mutate the result.
 */

/** Width the text box takes in a text+media split-scene layout. */
export const TEXT_BOX_DEFAULT_WIDTH = 0.22

/** "Text" option: one full-bleed text box plus its three placeholder lines. */
export const getTextSceneTemplate = () => getTextBoxInitialState({})

/** "Image" option: a single full-bleed image row. */
export const getImageSceneTemplate = () => ({
  ...IMAGE_SCENE,
})

/**
 * "Blank" option: an empty full-bleed image row. VideoTable's checkFilenameWithRowType
 * validates Image rows by their file extension, so this needs a real .png — public/images/
 * ships a transparent-pixel.png for exactly this, served at /images/transparent-pixel.png.
 */
export const BLANK_PLACEHOLDER_PATH = '/images/transparent-pixel.png'

/** "Blank" option: a single full-bleed transparent placeholder row. */
export const getBlankSceneTemplate = () => ({
  ...IMAGE_SCENE,
  Filename: BLANK_PLACEHOLDER_PATH,
  Original_File_Url: BLANK_PLACEHOLDER_PATH,
})

/** "Text + Image" option: text box on the left half, image on the right half. */
export const getTextImageSceneTemplate = () => [
  ...getTextBoxInitialState({
    AlignH: 'Center-LH',
  }, {
    Width: TEXT_BOX_DEFAULT_WIDTH,
  }),
  {
    ...IMAGE_SCENE,
    creationOffset: 1,
    AlignH: 'Center-RH',
    Width: 0.50,
  },
]

/**
 * The two catalog entries that do not build a scene themselves: they open a prompt
 * modal that generates one (POR-14173). Carried as `action` instead of `template`.
 */
export const SCENE_ACTION_AI_VIDEO = 'ai-video'
export const SCENE_ACTION_AI_IMAGE = 'ai-image'

/**
 * Everything the "Add scene" popup offers, in the order it is shown (POR-14173).
 *
 * `primary` is the top grid; `split` is the collapsible "Split layouts" section —
 * the six two-column layouts, which used to sit inline with the rest.
 *
 * A factory rather than a frozen constant on purpose: the helpers above hand back
 * fresh rows per call (see this file's header), and callers mutate what they get —
 * `addScene` assigns Scene/Subscene/Name straight onto these objects. A shared
 * module-level object would leak one insertion's numbering into the next.
 *
 * Entry shape:
 *   key      stable identifier, used for :key and for tests
 *   label    what the tile reads (POR-14173 — the old picker had icons only)
 *   types    TYPES_CONFIG entries, one per icon drawn in the tile
 *   template rows handed to `add-scene`; absent for entries with a handler/action
 *   type     TYPE_SUBSCENE_* for entries routed to their own emit (audio)
 *   scene    the single row those entries emit
 *   action   SCENE_ACTION_*; opens a modal instead of adding a scene
 *   ai       draws the sparkle badge on the tile
 *   applicable  the template can replace the selected scene's layout (POR-14877)
 */
export const getSceneCatalog = () => ({
  primary: [
    {
      key: 'aiVideo',
      label: 'AI video',
      types: [TYPES_CONFIG.VIDEO],
      action: SCENE_ACTION_AI_VIDEO,
      ai: true,
    },
    {
      key: 'aiImage',
      label: 'AI image',
      types: [TYPES_CONFIG.IMAGE],
      action: SCENE_ACTION_AI_IMAGE,
      ai: true,
    },
    {
      key: 'blank',
      label: 'Blank',
      types: [TYPES_CONFIG.BLANK],
      template: getBlankSceneTemplate(),
    },
    {
      key: 'video',
      label: 'Video',
      applicable: true,
      types: [TYPES_CONFIG.VIDEO],
      template: {
        ...VIDEO_SCENE,
      },
    },
    {
      key: 'image',
      label: 'Image',
      applicable: true,
      types: [TYPES_CONFIG.IMAGE],
      template: getImageSceneTemplate(),
    },
    {
      key: 'text',
      label: 'Text',
      applicable: true,
      types: [TYPES_CONFIG.TEXT],
      template: getTextSceneTemplate(),
    },
    {
      key: 'textOverImage',
      label: 'Image + text',
      applicable: true,
      types: [TYPES_CONFIG.TEXT_OVER_IMAGE],
      template: [
        {
          ...IMAGE_SCENE,
        },
        ...getTextBoxInitialState({
          creationOffset: 1,
          Top: '',
        }, {
          Width: TEXT_BOX_DEFAULT_WIDTH,
          Height: '',
        }),
      ],
    },
    {
      key: 'gallery',
      label: 'Gallery',
      types: [TYPES_CONFIG.MULTIPLE_IMAGE_SCENE],
    },
    {
      key: 'audio',
      label: 'Audio',
      types: [TYPES_CONFIG.MUSIC],
      type: TYPE_SUBSCENE_MUSIC,
      scene: {
        ...MUSIC_SCENE,
      },
    },
  ],
  split: [
    {
      key: 'textVideo',
      label: 'Text + Video',
      applicable: true,
      types: [TYPES_CONFIG.TEXT, TYPES_CONFIG.VIDEO],
      template: [
        ...getTextBoxInitialState({
          AlignH: 'Center-LH',
        }, {
          Width: TEXT_BOX_DEFAULT_WIDTH,
        }),
        {
          ...VIDEO_SCENE,
          creationOffset: 1,
          AlignH: 'Center-RH',
          Width: 0.50,
        },
      ],
    },
    {
      key: 'videoText',
      label: 'Video + Text',
      applicable: true,
      types: [TYPES_CONFIG.VIDEO, TYPES_CONFIG.TEXT],
      template: [
        {
          ...VIDEO_SCENE,
          AlignH: 'Center-LH',
          Width: 0.50,
        },
        ...getTextBoxInitialState({
          creationOffset: 1,
          AlignH: 'Center-RH',
        }, {
          Width: TEXT_BOX_DEFAULT_WIDTH,
        }),
      ],
    },
    {
      key: 'textImage',
      label: 'Text + Image',
      applicable: true,
      types: [TYPES_CONFIG.TEXT, TYPES_CONFIG.IMAGE],
      template: getTextImageSceneTemplate(),
    },
    {
      key: 'imageText',
      label: 'Image + Text',
      applicable: true,
      types: [TYPES_CONFIG.IMAGE, TYPES_CONFIG.TEXT],
      template: [
        {
          ...IMAGE_SCENE,
          AlignH: 'Center-LH',
          Width: 0.50,
        },
        ...getTextBoxInitialState({
          creationOffset: 1,
          AlignH: 'Center-RH',
        }, {
          Width: TEXT_BOX_DEFAULT_WIDTH,
        }),
      ],
    },
    {
      key: 'imageVideo',
      label: 'Image + Video',
      applicable: true,
      types: [TYPES_CONFIG.IMAGE, TYPES_CONFIG.VIDEO],
      template: [
        {
          ...IMAGE_SCENE,
          AlignH: 'Center-LH',
          Width: 0.50,
        }, {
          ...VIDEO_SCENE,
          creationOffset: 1,
          AlignH: 'Center-RH',
          Width: 0.50,
        },
      ],
    },
    {
      key: 'videoImage',
      label: 'Video + Image',
      applicable: true,
      types: [TYPES_CONFIG.VIDEO, TYPES_CONFIG.IMAGE],
      template: [
        {
          ...VIDEO_SCENE,
          AlignH: 'Center-LH',
          Width: 0.50,
        }, {
          ...IMAGE_SCENE,
          creationOffset: 1,
          AlignH: 'Center-RH',
          Width: 0.50,
        },
      ],
    },
  ],
})
